/**
 * Formats the given date in the given format.
 *
 * @param date The date to format
 * @param format The format to render the date in
 */
function formatDateWithTimeZone(date: Date, format: string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  const tokens: Record<string, string> = {
    yyyy: String(dateObj.getFullYear()),
    MM: String(dateObj.getMonth() + 1).padStart(2, '0'),
    dd: String(dateObj.getDate()).padStart(2, '0'),
    HH: String(dateObj.getHours()).padStart(2, '0'),
    mm: String(dateObj.getMinutes()).padStart(2, '0'),
    ss: String(dateObj.getSeconds()).padStart(2, '0')
  };
  return format.replace(/yyyy|MM|dd|HH|mm|ss/g, function (match) {
    return tokens[match];
  });
}

/**
 * Formats the given date in the given format.
 *
 * @param date The date to format
 * @param format The format to render the date in
 */
function getTimestampString(date: string | Date, format?: string): string {
  const dateObj = new Date(date);
  if (format === undefined) {
    format = 'yyyy-MM-dd';
  }
  return formatDateWithTimeZone(dateObj, format);
}

/**
 * Formats the given date in the given format and updates the html element with the given id with the formatted date.
 * @param date The date to format
 * @param id The id of the html element to put the output in
 * @param format The format to render the date in
 */
function setTimestampString(date: string | Date, id: string, format?: string): void {
  const element = document.getElementById(id);
  if (element) {
    element.innerText = getTimestampString(date, format);
  }
}

/**
 * Formats relative time (e.g., "2 days ago", "in 3 hours")
 */
function formatRelativeTime(date: Date): string {
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const now = new Date();
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const divisions: Array<{ amount: number; name: Intl.RelativeTimeFormatUnit }> = [
    { amount: 60, name: 'second' as Intl.RelativeTimeFormatUnit },
    { amount: 60, name: 'minute' as Intl.RelativeTimeFormatUnit },
    { amount: 24, name: 'hour' as Intl.RelativeTimeFormatUnit },
    { amount: 7, name: 'day' as Intl.RelativeTimeFormatUnit },
    { amount: 4.34524, name: 'week' as Intl.RelativeTimeFormatUnit },
    { amount: 12, name: 'month' as Intl.RelativeTimeFormatUnit },
    { amount: Number.POSITIVE_INFINITY, name: 'year' as Intl.RelativeTimeFormatUnit }
  ];
  let duration = diffSeconds;
  for (let i = 0; i < divisions.length; i++) {
    if (Math.abs(duration) < divisions[i].amount) {
      return rtf.format(Math.round(duration), divisions[i].name);
    }
    duration /= divisions[i].amount;
  }
  return rtf.format(Math.round(duration), 'year' as Intl.RelativeTimeFormatUnit);
}

/**
 * Formats the given date as relative time and returns HTML with Flowbite tooltip.
 * Returns a single HTML string that can be rendered in place.
 * 
 * @param date The date to format (UTC date string)
 * @param elementId Optional element ID to use for generating unique tooltip ID
 * @returns HTML string containing the relative time with Flowbite tooltip markup
 */
function getTimeElapsedString(date: string | Date, elementId?: string): string {
  const dateObj = new Date(date);
  const relativeTime = formatRelativeTime(dateObj);
  const formattedDate = getTimestampString(date, 'yyyy-MM-dd');
  
  // Generate unique tooltip ID
  const tooltipId = elementId ? `${elementId}-tooltip` : `tooltip-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  
  // Return HTML with Flowbite tooltip structure
  // The trigger element and tooltip div are both included in the returned string
  return `<span data-tooltip-target="${tooltipId}" data-tooltip-placement="top">${relativeTime}</span>
<div id="${tooltipId}" role="tooltip" class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-dark rounded-base shadow-xs opacity-0 tooltip transition-opacity duration-300">
    ${formattedDate}
    <div class="tooltip-arrow" data-popper-arrow></div>
</div>`;
}

/**
 * Formats a date in locale-specific numeric format (e.g., "05/02/2026")
 * @param date The date to format (UTC date string or Date object)
 * @returns Formatted date string in locale-specific format
 */
function formatDateLocale(date: string | Date): string {
  const dateObj = new Date(date);
  const formatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(dateObj);
}

/**
 * Formats a date in locale-specific numeric format and sets it to an element
 * @param date The date to format (UTC date string or Date object)
 * @param elementId The ID of the element to update
 */
function setDateLocale(date: string | Date, elementId: string): void {
  const element = document.getElementById(elementId);
  if (element) {
    element.textContent = formatDateLocale(date);
  }
}

/**
 * Formats a date and time in locale-specific format (e.g., "02/05/2026 at 16:24" for Belgium, "02/05/2026 at 4:46 PM" for US)
 * @param date The date to format (UTC date string or Date object)
 * @returns Formatted date and time string in locale-specific format
 */
function formatDateAndTimeLocale(date: string | Date): string {
  const dateObj = new Date(date);
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
  const formattedDate = dateFormatter.format(dateObj);
  const formattedTime = timeFormatter.format(dateObj);
  return `${formattedDate} at ${formattedTime}`;
}

/**
 * Sets relative time or formatted date based on age
 * If date is less than 1 year ago, shows relative time (e.g., "2 minutes ago")
 * Otherwise shows formatted date (e.g., "02/05/2025")
 * @param date The date to format (UTC date string or Date object)
 * @param elementId The ID of the element to update
 */
function setRelativeDateOrFormattedDate(date: string | Date, elementId: string): void {
  const element = document.getElementById(elementId);
  if (!element) {
    return;
  }

  const dateObj = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);

  if (diffYears >= 1) {
    // More than a year ago - show formatted date
    element.textContent = formatDateLocale(date);
  } else {
    // Less than a year ago - show relative time
    element.textContent = formatRelativeTime(dateObj);
  }
}

/**
 * Sets tooltip content with date type prefix, formatted date, and time
 * Format: "${dateTypePrefix} ${date} at ${time}"
 * Example: "Last reply: 02/05/2026 at 16:24"
 * @param date The date to format (UTC date string or Date object)
 * @param tooltipId The ID of the tooltip element to update
 * @param dateTypePrefix The prefix text (e.g., "Last reply", "Start date")
 */
/**
 * Sets the HTML5 `min` attribute on a date input to today's date (local calendar day, yyyy-MM-dd).
 */
function setDateInputMinToToday(target: string | HTMLInputElement): void {
  const input = typeof target === 'string' ? (document.getElementById(target) as HTMLInputElement | null) : target;
  if (!input || input.type !== 'date') {
    return;
  }
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  input.min = `${today.getFullYear()}-${month}-${day}`;
}

function setDateTooltip(date: string | Date, tooltipId: string, dateTypePrefix: string): void {
  const tooltipElement = document.getElementById(tooltipId);
  if (!tooltipElement) {
    return;
  }

  const formattedDateAndTime = formatDateAndTimeLocale(date);
  // Set text content, preserving the tooltip arrow div
  const arrowDiv = tooltipElement.querySelector('.tooltip-arrow');
  tooltipElement.textContent = dateTypePrefix
    ? `${dateTypePrefix}: ${formattedDateAndTime}`
    : formattedDateAndTime;
  // Re-add the arrow div after setting text content
  if (arrowDiv) {
    tooltipElement.appendChild(arrowDiv);
  } else {
    const newArrowDiv = document.createElement('div');
    newArrowDiv.className = 'tooltip-arrow';
    newArrowDiv.setAttribute('data-popper-arrow', '');
    tooltipElement.appendChild(newArrowDiv);
  }
}

// Export functions for use in other modules
export {
  formatDateWithTimeZone,
  getTimestampString,
  setTimestampString,
  formatRelativeTime,
  getTimeElapsedString,
  formatDateLocale,
  setDateLocale,
  formatDateAndTimeLocale,
  setRelativeDateOrFormattedDate,
  setDateTooltip,
  setDateInputMinToToday
};

// Also expose to global scope for JSP compatibility
declare global {
  interface Window {
    getTimeElapsedString: typeof getTimeElapsedString;
    getTimestampString: typeof getTimestampString;
    setTimestampString: typeof setTimestampString;
    formatDateLocale: typeof formatDateLocale;
    setDateLocale: typeof setDateLocale;
    formatDateAndTimeLocale: typeof formatDateAndTimeLocale;
    setRelativeDateOrFormattedDate: typeof setRelativeDateOrFormattedDate;
    setDateTooltip: typeof setDateTooltip;
  }
}

// Make functions available globally
window.getTimeElapsedString = getTimeElapsedString;
window.getTimestampString = getTimestampString;
window.setTimestampString = setTimestampString;
window.formatDateLocale = formatDateLocale;
window.setDateLocale = setDateLocale;
window.formatDateAndTimeLocale = formatDateAndTimeLocale;
window.setRelativeDateOrFormattedDate = setRelativeDateOrFormattedDate;
window.setDateTooltip = setDateTooltip;
